import { Component, computed, effect, inject, input, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NumberOnlyDirective } from '@app/core/directives/number-only-directive';
import { CustomCtaBaseDirective, ECustomFieldType } from '@core/directives/custom-cta-base.directive';
import { IGame } from '@core/interfaces/game.interface';
import { CustomValuesService } from '@core/services/custom-values.service';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { CustomRemoveButtonComponent } from '@shared/components/custom-remove-button/custom-remove-button.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EditableTitleComponent } from '@shared/components/editable-title/editable-title.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { ImageUtilitiesService } from '@utilities/image-utilities.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { debounceTime, distinctUntilChanged, fromEvent, map, Subscription } from 'rxjs';
import { IExecution } from '../../../execution-step/execution-list/execution/execution.interface';
import { DeliverableAdvancedButtonComponent } from '../advanced-button/deliverable-advanced-button.component';
import { DeliverableAdvancedSettingsSectionComponent } from '../advanced-settings-section/deliverable-advanced-settings-section.component';
import { IGameNameControl } from './game-play.interface';
import { GamePlayStore } from './game-play.store';

@Component({
  imports: [
    DeliverableAdvancedButtonComponent,
    DeliverableAdvancedSettingsSectionComponent,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatErrorComponent,
    NumberOnlyDirective,
    MatProgressSpinnerModule,
    MatTooltipModule,
    DotsSpinnerComponent,
    MatCheckboxModule,
    EditableTitleComponent,
    MatExpansionModule,
    NgxSkeletonLoaderModule,
    IconComponent,
    CustomRemoveButtonComponent,
  ],
  providers: [GamePlayStore],
  selector: 'app-game-play',
  templateUrl: './game-play.component.html',
})
export class GamePlayComponent extends CustomCtaBaseDirective implements OnInit, OnDestroy {
  public readonly platformFeature = input<EPlatformFeature | null | undefined>(null);
  public readonly executionValues = input.required<IExecution[]>();

  public readonly gameImageUrl = signal<string | null>(null);

  public readonly store = inject(GamePlayStore);

  public readonly games = computed(() =>
    this.store.games$().map((game) => ({
      ...game,
      coverUrlFormatted: ImageUtilitiesService.replaceImageDimensions(game.coverUrl, 24, 32),
    })),
  );
  public readonly isGamesLoading = this.store.loadingGames$;
  public readonly hasMoreGames = this.store.hasMore$;

  protected readonly customValueConfig = {
    componentFields: {
      getForm: (): FormGroup => this.form(),
      managedFields: [
        { name: 'gameName', type: ECustomFieldType.string },
        { name: 'duration', type: ECustomFieldType.number },
        { name: 'id', type: ECustomFieldType.string },
        { name: 'sponsoredContentRelevant', type: ECustomFieldType.boolean },
      ],
    },
  };

  private nameControlValueSubscription: Subscription | undefined;
  private durationControlSubscription: Subscription | undefined;
  private autocompleteScrollSubscription: Subscription | undefined;

  private readonly autocomplete = viewChild.required<MatAutocomplete>('gameAuto');

  private imageSetByOptionSelection = false;
  private hasSelectedGame = true;
  private hasSearched = false;

  constructor(customValuesService: CustomValuesService) {
    super(customValuesService);
    this.createDurationControlEffect();
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    this.createNameControlValueSubscription();
    this.autoOpenAdvancedSettingsIfNeeded(this.platformFeature(), this.form().get('type')?.value as EDeliverableType);
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.nameControlValueSubscription?.unsubscribe();
    this.durationControlSubscription?.unsubscribe();
    this.autocompleteScrollSubscription?.unsubscribe();
  }

  public override beforeModeChange(): void {
    if (!this.form().get('exclusiveTo')?.value) {
      this.form().get('gameName')?.disable({ emitEvent: false });
    }
  }

  public override afterDeleteCheckboxChange(): void {
    this.form().get('gameName')?.disable({ emitEvent: false });
  }

  public onGameInputFocus(): void {
    if (this.isViewMode()) {
      return;
    }

    const currentOptionsLength = this.games().length;

    if (currentOptionsLength === 0 && !this.isGamesLoading()) {
      const value = (this.form().get('gameName')?.value as string | null) ?? '';
      this.store.setSearchTerm(value);
      this.store.resetPagination();
      this.store.fetchPage({ reset: true });
    }
  }

  public onGameSelected(game: IGame): void {
    this.hasSelectedGame = true;
    this.hasSearched = false;
    this.imageSetByOptionSelection = true;

    const form = this.form();
    // gameId first: gameName's own validator (requires gameId to be set) runs synchronously
    // inside its setValue below and must see the fresh gameId, not the stale one.
    form.get('gameId')?.setValue(game.twitchId, { emitEvent: false });
    form.get('gameName')?.setValue(game.name);
    this.updateGameImage(game.coverUrl);
  }

  public onGameInputBlur(): void {
    if (this.isViewMode() || this.hasSelectedGame || !this.hasSearched) {
      return;
    }

    const form = this.form();
    const gameNameControl = form.get('gameName');

    this.updateGameImage(null);
    form.get('gameId')?.setValue(null, { emitEvent: false });

    gameNameControl?.setValue('', { emitEvent: false });
    gameNameControl?.setErrors({ required: true });
    gameNameControl?.markAsTouched();
    gameNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  public onAutocompleteOpened(): void {
    const panelId = this.autocomplete().id as string;
    const panel = document.getElementById(panelId) as HTMLElement | null;

    if (!panel) {
      return;
    }

    this.autocompleteScrollSubscription?.unsubscribe();

    this.autocompleteScrollSubscription = fromEvent(panel, 'scroll').subscribe(() => {
      const atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 100;

      if (atBottom && !this.isGamesLoading() && this.hasMoreGames()) {
        this.store.fetchMore();
      }
    });

    if (!this.isGamesLoading() && this.hasMoreGames() && panel.scrollHeight <= panel.clientHeight + 1) {
      this.store.fetchMore();
    }
  }

  public checkGameNameAvailability(): void {
    this.store.checkGameNameAvailability(this.form().get('gameName')?.value as string);
  }

  public onGameInputChange(): void {
    this.hasSearched = true;
    this.hasSelectedGame = false;
  }

  private updateGameImage(imageUrl: string | null): void {
    this.form()
      .get('boxArtUrl')
      ?.setValue(imageUrl ? ImageUtilitiesService.replaceImageDimensions(imageUrl, 36, 48) : null, {
        emitEvent: false,
      });
    this.gameImageUrl.set(imageUrl ? ImageUtilitiesService.replaceImageDimensions(imageUrl, 36, 48) : '-');
  }

  private createDurationControlEffect(): void {
    effect(() => {
      if (this.isViewMode()) {
        return;
      }

      const executionDurations = this.executionValues().reduce((acc, execution) => {
        const { duration } = execution;

        if (duration !== null) {
          acc.push(parseFloat(duration.toString()));
        }

        return acc;
      }, [] as number[]);

      this.checkDuration(executionDurations);
      this.durationControlSubscription?.unsubscribe();
      this.durationControlSubscription = this.form()
        ?.get('duration')
        ?.valueChanges?.subscribe(() => {
          this.checkDuration(executionDurations);
        });
    });
  }

  private createNameControlValueSubscription(): void {
    const nameControl = this.form().get('gameName') as IGameNameControl;

    if (!nameControl) {
      return;
    }

    this.nameControlValueSubscription = nameControl.valueChanges.subscribe(() => {
      this.hasSelectedGame = true;
      this.hasSearched = false;
    });

    this.nameControlValueSubscription = nameControl.valueChanges
      .pipe(
        map((v: string | null) => v ?? ''),
        debounceTime(600),
        distinctUntilChanged(),
      )
      .subscribe((value: string) => {
        if (this.imageSetByOptionSelection) {
          this.imageSetByOptionSelection = false;

          return;
        }

        this.gameImageUrl.set('-');

        if (!this.isViewMode()) {
          this.store.setSearchTerm(value);
          this.store.resetPagination();
          this.store.fetchPage({ reset: true });
        }
      });
  }

  private checkDuration(executionDurations: number[]): void {
    const durationControl = this.form().get('duration');

    if (!durationControl) {
      return;
    }

    const gamePlayDuration = parseFloat(durationControl.value);

    for (const duration of executionDurations) {
      if (durationControl.value === '' || isNaN(gamePlayDuration)) {
        durationControl.setErrors({ required: true });
      } else {
        const isValid = gamePlayDuration <= duration;
        durationControl.setErrors(isValid ? null : { durationDoesNotFitExecutions: true });

        if (!isValid) {
          break;
        }
      }
    }
  }
}
